
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuditLogs } from "@/hooks/useAuditLogs";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { TestResponse } from "@/types/test-response";
import { Loader2 } from "lucide-react";

interface ChangeHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  response: TestResponse;
}

export function ChangeHistoryDrawer({
  open,
  onOpenChange,
  response,
}: ChangeHistoryDrawerProps) {
  const { data: logs, isLoading } = useAuditLogs(response.id);

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'status_change':
        return <Badge variant="outline">Status Change</Badge>;
      case 'notes_update':
        return <Badge variant="secondary">Notes Update</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const formatValue = (value: string | null) => {
    return value || <span className="text-muted-foreground italic">None</span>;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] bg-card">
        <SheetHeader>
          <SheetTitle>Change History</SheetTitle>
          <SheetDescription>
            Recruitment changes for {response.candidate.full_name}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading history...</span>
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No changes recorded yet
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    {getActionBadge(log.action)}
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(log.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </span>
                  </div>
                  
                  <div className="text-sm">
                    <span className="font-medium">
                      {log.changer_profile?.full_name || log.changer_profile?.email || 'Unknown user'}
                    </span>
                    {' '}changed{' '}
                    <span className="font-medium">{log.field_name.replace('recruitment_', '')}</span>
                  </div>

                  <div className="text-sm space-y-1 bg-muted/30 rounded p-2">
                    <div>
                      <span className="text-muted-foreground">From:</span>{' '}
                      {formatValue(log.old_value)}
                    </div>
                    <div>
                      <span className="text-muted-foreground">To:</span>{' '}
                      {formatValue(log.new_value)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
